import React from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/useAuth";
import { useNATSStatus } from "../hooks/useNATSStatus";
import { useNavigate } from "react-router-dom";
import { useSAPDataStore } from "../store/SAPDataStore";
import { sidebarStyles, headerStyles, styles, cx } from "../styles";

// Animated Hamburger Icon component (same as in Header)
function HamburgerIcon({ open }: { open: boolean }) {
  return (
    <span className={headerStyles.hamburger.icon}>
      <span
        className={cx(
          headerStyles.hamburger.line,
          open
            ? headerStyles.hamburger.states.open.top
            : headerStyles.hamburger.states.closed.top,
        )}
      />
      <span
        className={cx(
          headerStyles.hamburger.line,
          open
            ? headerStyles.hamburger.states.open.middle
            : headerStyles.hamburger.states.closed.middle,
        )}
      />
      <span
        className={cx(
          headerStyles.hamburger.line,
          open
            ? headerStyles.hamburger.states.open.bottom
            : headerStyles.hamburger.states.closed.bottom,
        )}
      />
    </span>
  );
}

const MENU_ITEMS = [
  { label: "home", view: "home" },
  { label: "cockpitSessions", view: "cockpit/sessions" },
  { label: "compareMachine", view: "compare-machine" },
  { label: "settings", view: "settings" },
  { label: "help", view: "help" },
];

const SidebarMenu: React.FC<{
  currentView: string;
  open: boolean;
  onClose: () => void;
}> = ({ currentView, open, onClose }) => {
  const { t } = useTranslation();
  const { user, logout, accountManagement } = useAuth();
  const natsStatus = useNATSStatus();
  const navigate = useNavigate();
  const { getHomeViewState, showOnlyWithData } = useSAPDataStore();

  const handleMenuNavigation = (view: string) => {
    if (view === "home") {
      // For home navigation, restore saved HomeView state
      const savedState = getHomeViewState();
      console.log("🏠 SidebarMenu: Navigating to home with saved state:", {
        activeTab: savedState.activeTab,
        viewMode: savedState.viewMode,
        expandedNodesCount: savedState.expandedNodes.size,
      });

      // Build URL with saved state parameters
      const searchParams = new URLSearchParams();
      searchParams.set("tab", savedState.activeTab);
      searchParams.set("viewMode", savedState.viewMode);
      searchParams.set("showOnlyWithData", showOnlyWithData ? "true" : "false");

      // Convert expanded nodes to URL parameter
      const expandedNodesArray = Array.from(savedState.expandedNodes);
      if (expandedNodesArray.length > 0) {
        const expandedNodesString = expandedNodesArray.join("|");
        const encodedExpandedNodes = encodeURIComponent(expandedNodesString);
        searchParams.set("expandedNodes", encodedExpandedNodes);
      }

      navigate(`/?${searchParams.toString()}`);
    } else {
      navigate(`/${view}`);
    }
    onClose();
  };

  const handleLogout = () => {
    logout();
  };

  const handleAccountManagement = () => {
    accountManagement();
  };

  // Get user info from auth service
  const userName = user?.name || user?.username || "User";
  const userEmail = user?.email || "";
  const userPicture = user?.picture;

  // Generate initials from user name
  const getInitials = (name: string): string => {
    if (!name) return "U";

    const nameParts = name.trim().split(" ");
    if (nameParts.length === 1) {
      // Single name - take first letter
      return nameParts[0].charAt(0).toUpperCase();
    } else {
      // Multiple names - take first letter of first and last name
      const firstName = nameParts[0];
      const lastName = nameParts[nameParts.length - 1];
      return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
    }
  };

  const userInitials = getInitials(userName);

  return (
    <>
      {/* Invisible overlay that closes menu when clicked */}
      {open && (
        <div
          className={sidebarStyles.overlay}
          onClick={onClose}
          aria-label="Close menu overlay"
        />
      )}

      {/* Sidebar Menu */}
      <div
        className={cx(
          sidebarStyles.container,
          open ? sidebarStyles.open : sidebarStyles.closed,
        )}
        style={{ minWidth: 280 }}
      >
        {/* Profile section */}
        <div className={sidebarStyles.profile.container}>
          <div className={sidebarStyles.profile.avatar.container}>
            {userPicture ? (
              <img
                src={userPicture}
                alt={userName}
                className={sidebarStyles.profile.avatar.image}
                onError={(e) => {
                  // Fallback to initials avatar if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = "none";
                  const initialsDiv =
                    target.nextElementSibling as HTMLDivElement;
                  if (initialsDiv) {
                    initialsDiv.classList.remove("hidden");
                  }
                }}
              />
            ) : null}
            <div
              className={cx(
                sidebarStyles.profile.avatar.fallback,
                userPicture ? "hidden" : "",
              )}
            >
              {userInitials}
            </div>
          </div>
          <div className={sidebarStyles.profile.name}>{userName}</div>
          <div className={sidebarStyles.profile.email}>{userEmail}</div>
          <button
            onClick={onClose}
            aria-label="Close menu"
            className={sidebarStyles.profile.closeButton}
          >
            <HamburgerIcon open={true} />
          </button>
        </div>
        <hr className={styles.sidebarMenu.divider} />
        {/* Menu items */}
        <ul className={styles.sidebarMenu.navList}>
          {/* Main menu items except settings/help */}
          {MENU_ITEMS.filter(
            (item) => item.view !== "settings" && item.view !== "help",
          ).map((item) => (
            <li key={item.view}>
              <button
                className={cx(
                  "w-full text-left px-4 py-2 rounded font-semibold transition-all duration-150 text-white flex items-center gap-3",
                  "cursor-pointer hover:scale-[1.03] active:scale-95 hover:shadow-lg focus-visible:ring-2 focus-visible:ring-cockpitblue-light focus-visible:outline-none",
                  currentView === item.view
                    ? "bg-cockpitblue"
                    : "hover:bg-cockpitblue-light/60",
                )}
                onClick={() => handleMenuNavigation(item.view)}
              >
                {item.view === "home" && (
                  <svg
                    className={styles.sidebarMenu.navIcon}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                )}
                {item.view === "cockpit/sessions" && (
                  <svg
                    className={styles.sidebarMenu.navIcon}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                )}
                {item.view === "compare-machine" && (
                  <svg
                    className={styles.sidebarMenu.navIcon}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                    />
                  </svg>
                )}
                {t(item.label)}
              </button>
            </li>
          ))}
          {/* Dashboard Editor - External Link */}
          <li>
            <a
              href="https://editor.enlixy.com"
              className={cx(
                styles.sidebarMenu.navItem,
                "cursor-pointer hover:scale-[1.03] active:scale-95 hover:shadow-lg focus-visible:ring-2 focus-visible:ring-cockpitblue-light focus-visible:outline-none flex items-center gap-3 w-full transition-all duration-150",
              )}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
            >
              <svg
                className={styles.sidebarMenu.navIcon}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
                />
              </svg>
              {t("dashboardEditor", "Dashboard Editor")}
            </a>
          </li>
          {/* Task Based Maintenance - Placeholder */}
          <li>
            <button
              className={cx(
                styles.sidebarMenu.navItem,
                "flex items-center gap-3 w-full cursor-not-allowed opacity-60",
              )}
              disabled
            >
              <svg
                className={styles.sidebarMenu.navIcon}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                />
              </svg>
              {t("taskBasedMaintenance", "Task Based Maintenance")}
            </button>
          </li>
          {/* Machine Acceptance - Placeholder */}
          <li>
            <button
              className={cx(
                styles.sidebarMenu.navItem,
                "flex items-center gap-3 w-full cursor-not-allowed opacity-60",
              )}
              disabled
            >
              <svg
                className={styles.sidebarMenu.navIcon}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                />
              </svg>
              {t("machineAcceptance", "Machine Acceptance")}
            </button>
          </li>
          {/* Anomaly Detection - Placeholder */}
          <li>
            <button
              className={cx(
                styles.sidebarMenu.navItem,
                "flex items-center gap-3 w-full cursor-not-allowed opacity-60",
              )}
              disabled
            >
              <svg
                className={styles.sidebarMenu.navIcon}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              {t("anomalyDetection", "Anomaly Detection")}
            </button>
          </li>
          {/* Machine Uploader - External Link (just before Settings) */}
          <li>
            <a
              href={
                import.meta.env.VITE_UPLOADER_URL ||
                "https://uploader.enlixy.com"
              }
              className={cx(
                styles.sidebarMenu.navItem,
                "cursor-pointer hover:scale-[1.03] active:scale-95 hover:shadow-lg focus-visible:ring-2 focus-visible:ring-cockpitblue-light focus-visible:outline-none flex items-center gap-3 w-full transition-all duration-150",
              )}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
            >
              <svg
                className={styles.sidebarMenu.navIcon}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              {t("machineUploader", "Machine Uploader")}
            </a>
          </li>
          {/* Settings and Help always last */}
          {MENU_ITEMS.filter(
            (item) => item.view === "settings" || item.view === "help",
          ).map((item) => (
            <li key={item.view}>
              <button
                className={cx(
                  "w-full text-left px-4 py-2 rounded font-semibold transition-all duration-150 text-white flex items-center gap-3",
                  "cursor-pointer hover:scale-[1.03] active:scale-95 hover:shadow-lg focus-visible:ring-2 focus-visible:ring-cockpitblue-light focus-visible:outline-none",
                  currentView === item.view
                    ? "bg-cockpitblue"
                    : "hover:bg-cockpitblue-light/60",
                )}
                onClick={() => handleMenuNavigation(item.view)}
              >
                {item.view === "settings" && (
                  <svg
                    className={styles.sidebarMenu.navIcon}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                )}
                {item.view === "help" && (
                  <svg
                    className={styles.sidebarMenu.navIcon}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                )}
                {t(item.label)}
              </button>
            </li>
          ))}
        </ul>

        {/* NATS Connection Status */}
        <div className={sidebarStyles.connectionStatus.container}>
          <div className={sidebarStyles.connectionStatus.indicator}>
            <div
              className={cx(
                sidebarStyles.connectionStatus.dot,
                natsStatus.isConnected
                  ? sidebarStyles.connectionStatus.connected
                  : sidebarStyles.connectionStatus.disconnected,
              )}
            />
            <div className={sidebarStyles.connectionStatus.text}>
              <div className={sidebarStyles.connectionStatus.title}>
                NATS:{" "}
                {natsStatus.isConnected ? t("connected") : t("disconnected")}
              </div>
              {natsStatus.error && (
                <div
                  className={cx(
                    sidebarStyles.connectionStatus.details,
                    "text-red-300 mt-1",
                  )}
                >
                  {t("connectionError")}
                </div>
              )}
              {!natsStatus.isConnected && natsStatus.lastConnected && (
                <div className={styles.sidebarMenu.connectionTime}>
                  {t("lastConnected")}:{" "}
                  {natsStatus.lastConnected.toLocaleTimeString()}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Account Management and Logout at bottom */}
        <div className="flex-shrink-0 px-6 pb-6 space-y-2">
          <button
            onClick={handleAccountManagement}
            className={styles.sidebarMenu.refreshButton}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            <span>{t("myAccount")}</span>
          </button>
          <button
            onClick={handleLogout}
            className={styles.sidebarMenu.refreshButton}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                d="M17 16l4-4m0 0l-4-4m4 4H7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <rect
                x="3"
                y="4"
                width="4"
                height="16"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
            <span>{t("logout")}</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default SidebarMenu;
